import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BarChart3,
  Users,
  DollarSign,
  Target,
  CheckCircle,
  Info,
  ArrowRight,
  MessageSquare,
  Route,
  MousePointer,
  Eye,
  Settings,
  LogOut,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

// Formatters
const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;
const pctDec = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;
const currency = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

// Low confidence threshold
const LOW_N_THRESHOLD = 20;

// Low confidence indicator for small sample sizes
function LowConfidenceBadge({ n }) {
  if (n >= LOW_N_THRESHOLD) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className="ml-1 bg-amber-500/20 text-amber-300 text-[10px] px-1 py-0">
            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
            Low N
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            Sample size ({n}) is below threshold ({LOW_N_THRESHOLD}). Results
            may be unreliable.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Attribution breakdown component
function AttributionBreakdown({ breakdown, compact = false }) {
  if (!breakdown || Object.keys(breakdown).length === 0) return null;

  const explicit = breakdown.explicit || 0;
  const leadMatch = breakdown.lead_match || 0;
  const ambiguous = breakdown.ambiguous || 0;
  const total = explicit + leadMatch + ambiguous;

  if (total === 0) return null;

  const explicitPct = Math.round((explicit / total) * 100);
  const inferredPct = Math.round((leadMatch / total) * 100);
  const ambiguousPct = Math.round((ambiguous / total) * 100);

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              className={`text-[10px] px-1.5 cursor-help ${
                explicitPct >= 80
                  ? "bg-emerald-500/20 text-emerald-300"
                  : ambiguousPct > 20
                  ? "bg-amber-500/20 text-amber-300"
                  : "bg-blue-500/20 text-blue-300"
              }`}
            >
              {explicitPct}% direct
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs space-y-1">
              <p>
                <span className="text-emerald-300">Explicit:</span> {explicit} (
                {explicitPct}%)
              </p>
              <p>
                <span className="text-blue-300">Inferred:</span> {leadMatch} (
                {inferredPct}%)
              </p>
              {ambiguous > 0 && (
                <p>
                  <span className="text-amber-300">Ambiguous:</span> {ambiguous}{" "}
                  ({ambiguousPct}%)
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Card className="glass border-white/10">
      <CardContent className="p-4">
        <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">
          Attribution
        </p>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-emerald-300 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Explicit
            </span>
            <span className="font-mono text-slate-100">{explicit}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-blue-300 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Inferred
            </span>
            <span className="font-mono text-slate-100">{leadMatch}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-amber-300 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Ambiguous
            </span>
            <span className="font-mono text-slate-100">{ambiguous}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Summary KPI Card
function SummaryCard({ title, value, subtitle, icon: Icon }) {
  return (
    <Card className="glass border-white/10" data-testid={`summary-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">
              {title}
            </p>
            <p className="text-xl font-bold text-slate-100 mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
            )}
          </div>
          {Icon && (
            <div className="rounded-lg bg-indigo-500/20 p-2">
              <Icon className="h-4 w-4 text-indigo-300" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Node Drop-off Panel
function NodeDropoffPanel({ options, nodeViews }) {
  // Calculate node-level metrics from options
  const nodeMetrics = useMemo(() => {
    if (!options || options.length === 0) return [];

    // Group options by from_node_id to get node-level stats
    const nodeMap = new Map();

    for (const opt of options) {
      const nodeId = opt.from_node_id;
      if (!nodeId) continue;

      if (!nodeMap.has(nodeId)) {
        nodeMap.set(nodeId, {
          node_id: nodeId,
          node_label: opt.option_label?.substring(0, 30) || nodeId.substring(0, 8),
          views: opt.node_views || 0,
          total_clicks: 0,
          downstream_runs: 0,
          downstream_booked: 0,
          downstream_submitted: 0,
          downstream_issued: 0,
        });
      }

      const node = nodeMap.get(nodeId);
      node.total_clicks += opt.option_clicks || 0;
      node.downstream_runs += opt.downstream_runs || 0;
      node.downstream_booked += opt.downstream_booked || 0;
      node.downstream_submitted += opt.downstream_submitted || 0;
      node.downstream_issued += opt.downstream_issued || 0;
    }

    // Calculate exit rate and convert to array
    const nodes = Array.from(nodeMap.values()).map((node) => ({
      ...node,
      exit_rate: node.views > 0 ? 1 - node.total_clicks / node.views : 0,
      booking_rate:
        node.downstream_runs > 0
          ? node.downstream_booked / node.downstream_runs
          : 0,
      submission_rate:
        node.downstream_runs > 0
          ? node.downstream_submitted / node.downstream_runs
          : 0,
      issue_rate:
        node.downstream_runs > 0
          ? node.downstream_issued / node.downstream_runs
          : 0,
    }));

    // Sort by exit rate (highest first)
    nodes.sort((a, b) => b.exit_rate - a.exit_rate);

    return nodes.slice(0, 10);
  }, [options]);

  if (nodeMetrics.length === 0) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="p-6 text-center">
          <LogOut className="h-10 w-10 mx-auto mb-3 text-slate-500" />
          <p className="text-slate-300">No drop-off data available.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="node-dropoff-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-100 flex items-center gap-2">
          <LogOut className="h-4 w-4 text-red-400" />
          Node Drop-off
          <span className="text-xs font-normal text-slate-400 ml-auto">
            Sorted by highest exit rate
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-slate-300">Node</TableHead>
                <TableHead className="text-slate-300 text-right">
                  Views
                </TableHead>
                <TableHead className="text-slate-300 text-right">
                  Exit %
                </TableHead>
                <TableHead className="text-slate-300 text-right">
                  Book %
                </TableHead>
                <TableHead className="text-slate-300 text-right">
                  Submit %
                </TableHead>
                <TableHead className="text-slate-300 text-right">
                  Issue %
                </TableHead>
                <TableHead className="text-slate-300 text-right">N</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nodeMetrics.map((node) => (
                <TableRow
                  key={node.node_id}
                  className="border-white/10 hover:bg-white/5"
                >
                  <TableCell className="max-w-[150px]">
                    <p
                      className="text-slate-100 text-sm truncate"
                      title={node.node_id}
                    >
                      {node.node_label}
                    </p>
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {node.views}
                  </TableCell>
                  <TableCell className="text-right">
                    <span
                      className={`font-mono ${
                        node.exit_rate >= 0.5
                          ? "text-red-400"
                          : node.exit_rate >= 0.3
                          ? "text-amber-400"
                          : "text-slate-100"
                      }`}
                    >
                      {pct(node.exit_rate)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {pct(node.booking_rate)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {pct(node.submission_rate)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {pct(node.issue_rate)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-slate-400 text-xs">
                      {node.downstream_runs}
                    </span>
                    <LowConfidenceBadge n={node.downstream_runs} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// Options Leaderboard Table
function OptionsLeaderboard({ options, sortBy, onSortChange }) {
  const sortedOptions = useMemo(() => {
    if (!options) return [];
    const sorted = [...options];

    switch (sortBy) {
      case "clicks":
        sorted.sort((a, b) => b.option_clicks - a.option_clicks);
        break;
      case "booking":
        sorted.sort((a, b) => b.downstream_booking_rate - a.downstream_booking_rate);
        break;
      case "revenue":
        sorted.sort(
          (a, b) =>
            b.downstream_commission_total / Math.max(b.downstream_runs, 1) -
            a.downstream_commission_total / Math.max(a.downstream_runs, 1)
        );
        break;
      case "dropoff":
        sorted.sort(
          (a, b) => 1 - a.option_click_rate - (1 - b.option_click_rate)
        );
        break;
      default:
        break;
    }

    return sorted;
  }, [options, sortBy]);

  if (!options || options.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <MousePointer className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No option data available for this period.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-400">Sort by:</span>
        <Select value={sortBy} onValueChange={onSortChange}>
          <SelectTrigger className="w-[180px] h-8 bg-white/5 text-slate-100 border-white/10 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="clicks">Most Clicks</SelectItem>
            <SelectItem value="booking">Best Booking Rate</SelectItem>
            <SelectItem value="revenue">Best Revenue/Run</SelectItem>
            <SelectItem value="dropoff">Worst Exit Impact</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-slate-300">Option</TableHead>
              <TableHead className="text-slate-300 text-right">Clicks</TableHead>
              <TableHead className="text-slate-300 text-right">CTR</TableHead>
              <TableHead className="text-slate-300 text-right">Book %</TableHead>
              <TableHead className="text-slate-300 text-right">Submit %</TableHead>
              <TableHead className="text-slate-300 text-right">Issue %</TableHead>
              <TableHead className="text-slate-300 text-right">Rev/Run</TableHead>
              <TableHead className="text-slate-300 text-right">N</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedOptions.slice(0, 15).map((opt, idx) => {
              const revenuePerRun =
                opt.downstream_runs > 0
                  ? opt.downstream_commission_total / opt.downstream_runs
                  : 0;

              return (
                <TableRow
                  key={opt.option_id}
                  className="border-white/10 hover:bg-white/5"
                >
                  <TableCell className="max-w-[220px]">
                    <div className="space-y-1">
                      <p
                        className="text-slate-100 text-sm truncate"
                        title={opt.option_label}
                      >
                        {opt.option_label || "Unnamed option"}
                      </p>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge
                          className={`text-[10px] ${
                            opt.option_type === "objection"
                              ? "bg-amber-500/20 text-amber-300"
                              : opt.option_type === "qualifying"
                              ? "bg-blue-500/20 text-blue-300"
                              : opt.option_type === "close"
                              ? "bg-emerald-500/20 text-emerald-300"
                              : "bg-slate-500/20 text-slate-300"
                          }`}
                        >
                          {opt.option_type}
                        </Badge>
                        {opt.objection_category && (
                          <Badge className="text-[10px] bg-purple-500/20 text-purple-300">
                            {opt.objection_category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {opt.option_clicks}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {pctDec(opt.option_click_rate)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span
                      className={`font-mono ${
                        opt.downstream_booking_rate >= 0.5
                          ? "text-emerald-400"
                          : opt.downstream_booking_rate >= 0.3
                          ? "text-slate-100"
                          : "text-red-400"
                      }`}
                    >
                      {pct(opt.downstream_booking_rate)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {pct(opt.downstream_submission_rate)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-100">
                    {pct(opt.downstream_issue_rate)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-emerald-400">
                    {currency(revenuePerRun)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-slate-400 text-xs">
                      n={opt.downstream_runs}
                    </span>
                    <LowConfidenceBadge n={opt.downstream_runs} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Objections Panel
function ObjectionsPanel({ objections, onConfigureClick }) {
  if (!objections || objections.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="objections-panel-empty">
        <CardContent className="p-6 text-center">
          <MessageSquare className="h-10 w-10 mx-auto mb-3 text-slate-500" />
          <p className="text-slate-300 mb-2">Not configured yet</p>
          <p className="text-sm text-slate-400 mb-4">
            Tag options as objections in the Script Builder to enable objection
            analytics.
          </p>
          <Button
            onClick={onConfigureClick}
            variant="outline"
            size="sm"
            className="bg-white/5 text-slate-100 border-white/10 hover:bg-white/10"
          >
            <Settings className="h-4 w-4 mr-2" />
            Configure Tags
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="objections-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-100 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-amber-400" />
          Objection Handling
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-slate-300">Category</TableHead>
              <TableHead className="text-slate-300 text-right">Freq</TableHead>
              <TableHead className="text-slate-300 text-right">
                Win (Book)
              </TableHead>
              <TableHead className="text-slate-300 text-right">
                Win (Issue)
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {objections.map((obj) => (
              <TableRow
                key={obj.objection_category}
                className="border-white/10 hover:bg-white/5"
              >
                <TableCell className="font-medium text-slate-100 capitalize">
                  {obj.objection_category.replace(/_/g, " ")}
                </TableCell>
                <TableCell className="text-right font-mono text-slate-100">
                  {pctDec(obj.frequency_rate)}
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={`font-mono ${
                      obj.win_rate_booked >= 0.5
                        ? "text-emerald-400"
                        : "text-slate-100"
                    }`}
                  >
                    {pct(obj.win_rate_booked)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={`font-mono ${
                      obj.win_rate_issued >= 0.3
                        ? "text-emerald-400"
                        : "text-slate-100"
                    }`}
                  >
                    {pct(obj.win_rate_issued)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// Paths Panel with tabs for volume vs revenue
function PathsPanel({ paths, totalRuns }) {
  const [sortBy, setSortBy] = useState("volume");

  const sortedPaths = useMemo(() => {
    if (!paths) return [];
    const sorted = [...paths];

    if (sortBy === "revenue") {
      sorted.sort((a, b) => b.revenue_per_run - a.revenue_per_run);
    } else {
      sorted.sort((a, b) => b.total_runs - a.total_runs);
    }

    return sorted.slice(0, 8);
  }, [paths, sortBy]);

  if (!paths || paths.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="paths-panel-empty">
        <CardContent className="p-6 text-center">
          <Route className="h-10 w-10 mx-auto mb-3 text-slate-500" />
          <p className="text-slate-300">No path data available.</p>
          <p className="text-sm text-slate-400 mt-2">
            Paths are generated from option click sequences.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="paths-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-slate-100 flex items-center gap-2">
            <Route className="h-4 w-4 text-indigo-400" />
            Top Paths
          </CardTitle>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[130px] h-7 bg-white/5 text-slate-100 border-white/10 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="volume">By Volume</SelectItem>
              <SelectItem value="revenue">By Revenue</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {sortedPaths.map((path, idx) => (
          <div
            key={path.path_id}
            className="p-3 rounded-lg bg-white/5 border border-white/5"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge className="bg-indigo-500/20 text-indigo-300">
                  #{idx + 1}
                </Badge>
                <span className="text-xs text-slate-400">
                  {path.total_runs} runs ({pctDec(path.frequency_rate)})
                </span>
                <LowConfidenceBadge n={path.total_runs} />
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-slate-400">
                  Book:{" "}
                  <span className="text-emerald-400">
                    {pct(path.conversion_booked)}
                  </span>
                </span>
                <span className="text-emerald-400 font-mono">
                  {currency(path.revenue_per_run)}/run
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {path.path_labels.slice(0, 5).map((label, i) => (
                <React.Fragment key={i}>
                  <Badge className="bg-slate-700/50 text-slate-200 text-xs px-2 max-w-[120px] truncate">
                    {label}
                  </Badge>
                  {i < Math.min(path.path_labels.length, 5) - 1 && (
                    <ArrowRight className="h-3 w-3 text-slate-500 flex-shrink-0" />
                  )}
                </React.Fragment>
              ))}
              {path.path_labels.length > 5 && (
                <Badge className="bg-slate-600/50 text-slate-300 text-xs">
                  +{path.path_labels.length - 5} more
                </Badge>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// Main Component
export default function ScriptIntelligence({ period: externalPeriod, campaignId }) {
  const [scripts, setScripts] = useState([]);
  const [selectedScriptId, setSelectedScriptId] = useState("");
  const [loading, setLoading] = useState(false);
  const [includeInferred, setIncludeInferred] = useState(true);

  const [optionsData, setOptionsData] = useState(null);
  const [objectionsData, setObjectionsData] = useState(null);
  const [pathsData, setPathsData] = useState(null);

  const [optionsSortBy, setOptionsSortBy] = useState("clicks");

  // Fetch available scripts
  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const res = await api.get("/scripts");
        const scriptList = res.data || [];
        setScripts(scriptList);
        if (scriptList.length > 0 && !selectedScriptId) {
          setSelectedScriptId(scriptList[0].id);
        }
      } catch (e) {
        console.error("Failed to fetch scripts:", e);
      }
    };
    fetchScripts();
  }, []);

  // Fetch analytics data when script/period/campaign changes
  // MA-IMPL-118B: Added campaignId filter support
  useEffect(() => {
    if (!selectedScriptId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const period = externalPeriod || "30d";
        const campaignParam = campaignId && campaignId !== "all" ? `&campaign_id=${campaignId}` : "";
        const [optRes, objRes, pathRes] = await Promise.all([
          api.get(
            `/analytics/scripts/options?script_id=${selectedScriptId}&period=${period}${campaignParam}`
          ),
          api.get(
            `/analytics/scripts/objections?script_id=${selectedScriptId}&period=${period}${campaignParam}`
          ),
          api.get(
            `/analytics/scripts/paths?script_id=${selectedScriptId}&period=${period}&min_runs=1${campaignParam}`
          ),
        ]);

        setOptionsData(optRes.data);
        setObjectionsData(objRes.data);
        setPathsData(pathRes.data);
      } catch (e) {
        console.error("Failed to fetch script analytics:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedScriptId, externalPeriod, campaignId]);

  // Calculate summary metrics
  const summary = useMemo(() => {
    if (!optionsData) return null;

    const options = optionsData.options || [];
    const breakdown = optionsData.attribution_breakdown || {};

    // Get unique runs from paths data (more accurate)
    const totalRuns = pathsData?.total_runs || 0;

    // Aggregate outcomes across all options (with deduplication consideration)
    let totalBooked = 0;
    let totalSubmitted = 0;
    let totalIssued = 0;
    let totalRevenue = 0;

    // Track unique runs for proper rate calculation
    const seenRuns = new Set();

    for (const opt of options) {
      if (opt.downstream_runs > 0) {
        totalBooked += opt.downstream_booked || 0;
        totalSubmitted += opt.downstream_submitted || 0;
        totalIssued += opt.downstream_issued || 0;
        totalRevenue += opt.downstream_commission_total || 0;
      }
    }

    // Use total_runs from paths as denominator (most accurate)
    const runCount = totalRuns > 0 ? totalRuns : 1;

    return {
      runs: totalRuns,
      bookingRate: totalRuns > 0 ? totalBooked / totalRuns : 0,
      submissionRate: totalBooked > 0 ? totalSubmitted / totalBooked : 0,
      issueRate: totalSubmitted > 0 ? totalIssued / totalSubmitted : 0,
      revenuePerRun: totalRuns > 0 ? totalRevenue / totalRuns : 0,
      breakdown,
    };
  }, [optionsData, pathsData]);

  // Filter options based on includeInferred toggle
  const filteredOptions = useMemo(() => {
    if (!optionsData?.options) return [];
    // Note: The API currently returns all outcomes. The toggle controls display behavior.
    // In a full implementation, you would re-fetch with a parameter or filter locally.
    // For now, we show all and indicate the toggle state.
    return optionsData.options;
  }, [optionsData, includeInferred]);

  const handleConfigureScriptBuilder = () => {
    if (selectedScriptId) {
      window.location.href = `/app/scripts/${selectedScriptId}/edit`;
    }
  };

  return (
    <div className="space-y-4" data-testid="script-intelligence">
      {/* Controls */}
      <Card className="glass border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-300">Script:</span>
              <Select
                value={selectedScriptId}
                onValueChange={setSelectedScriptId}
              >
                <SelectTrigger className="w-[220px] bg-white/5 text-slate-100 border-white/10">
                  <SelectValue placeholder="Select script..." />
                </SelectTrigger>
                <SelectContent>
                  {scripts.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="flex items-center gap-2">
                <Switch
                  id="include-inferred"
                  checked={includeInferred}
                  onCheckedChange={setIncludeInferred}
                  className="data-[state=checked]:bg-indigo-500"
                />
                <label
                  htmlFor="include-inferred"
                  className="text-sm text-slate-300 cursor-pointer"
                >
                  Include inferred
                </label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-slate-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[220px]">
                      <p className="text-xs">
                        <strong>ON:</strong> Includes outcomes matched by lead_id
                        (medium confidence).
                        <br />
                        <strong>OFF:</strong> Shows only explicitly linked
                        outcomes (high confidence).
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {summary && (
                <AttributionBreakdown breakdown={summary.breakdown} compact />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
        </div>
      ) : !selectedScriptId ? (
        <Card className="glass border-white/10">
          <CardContent className="p-8 text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-slate-500" />
            <p className="text-slate-300">Select a script to view analytics</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <SummaryCard
                title="Runs"
                value={summary.runs.toLocaleString()}
                icon={Users}
              />
              <SummaryCard
                title="Booking Rate"
                value={pct(summary.bookingRate)}
                subtitle="run-based"
                icon={Target}
              />
              <SummaryCard
                title="Submission Rate"
                value={pct(summary.submissionRate)}
                subtitle="of booked"
                icon={CheckCircle}
              />
              <SummaryCard
                title="Issue Rate"
                value={pct(summary.issueRate)}
                subtitle="of submitted"
                icon={CheckCircle}
              />
              <SummaryCard
                title="Rev/Run"
                value={currency(summary.revenuePerRun)}
                icon={DollarSign}
              />
              <AttributionBreakdown breakdown={summary.breakdown} />
            </div>
          )}

          {/* Node Drop-off Panel */}
          <NodeDropoffPanel options={filteredOptions} />

          {/* Main Content: Options + Side Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Options Leaderboard (Main) */}
            <div className="lg:col-span-8">
              <Card className="glass border-white/10" data-testid="options-leaderboard">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-slate-100 flex items-center gap-2">
                    <MousePointer className="h-4 w-4 text-indigo-400" />
                    Option Leaderboard
                    {includeInferred ? (
                      <Badge className="text-[10px] bg-blue-500/20 text-blue-300 ml-2">
                        incl. inferred
                      </Badge>
                    ) : (
                      <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 ml-2">
                        explicit only
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <OptionsLeaderboard
                    options={filteredOptions}
                    sortBy={optionsSortBy}
                    onSortChange={setOptionsSortBy}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Side Panels */}
            <div className="lg:col-span-4 space-y-4">
              {/* Objections */}
              <ObjectionsPanel
                objections={objectionsData?.objections}
                onConfigureClick={handleConfigureScriptBuilder}
              />

              {/* Paths */}
              <PathsPanel
                paths={pathsData?.paths}
                totalRuns={pathsData?.total_runs}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
